/**
 * AgentConfigForm — Formulaire de configuration typé par agent_id.
 *
 * Remplace le textarea JSON brut par des inputs Bootstrap typés.
 * Chaque agent a un schéma spécifique. PHI jamais dans les champs.
 * Bootstrap 5.3 + tokens --hrl-*. Zéro lib externe.
 *
 * Agents couverts :
 *   alert-agent / alertes-agent  → window_minutes, enabled
 *   coherence-agent              → check_interval_hours, enabled
 *   rpa-agent                    → hitl_required, enabled
 *   manager-agent                → mode, enabled
 *   *                            → JSON brut (fallback)
 */

import { useEffect, useState } from 'react';
import type { AgentConfig } from '../services/agentsAdminService';
import { agentsAdminService } from '../services/agentsAdminService';

interface Props {
  agentId: string;
}

// ── Schémas par agent_id ──────────────────────────────────────────────────────

type FieldType = 'toggle' | 'number' | 'select';

interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  description?: string;
  min?: number;
  max?: number;
  step?: number;
  options?: { value: string; label: string }[];
}

const SCHEMAS: Record<string, FieldDef[]> = {
  'alert-agent': [
    {
      key: 'enabled',
      label: 'Activé',
      type: 'toggle',
      description: 'Active ou désactive la détection automatique.',
    },
    {
      key: 'window_minutes',
      label: 'Fenêtre d\'analyse (minutes)',
      type: 'number',
      description: 'Tracabilités analysées sur les N dernières minutes.',
      min: 30,
      max: 480,
      step: 30,
    },
  ],
  'alertes-agent': [
    {
      key: 'enabled',
      label: 'Activé',
      type: 'toggle',
    },
    {
      key: 'window_minutes',
      label: 'Fenêtre d\'analyse (minutes)',
      type: 'number',
      min: 30,
      max: 480,
      step: 30,
    },
  ],
  'coherence-agent': [
    {
      key: 'enabled',
      label: 'Activé',
      type: 'toggle',
      description: 'Active ou désactive la vérification de cohérence.',
    },
    {
      key: 'check_interval_hours',
      label: 'Intervalle de contrôle (heures)',
      type: 'number',
      description: 'Fréquence des vérifications (4 = 4×/jour).',
      min: 1,
      max: 24,
      step: 1,
    },
  ],
  'rpa-agent': [
    {
      key: 'enabled',
      label: 'Activé',
      type: 'toggle',
    },
    {
      key: 'hitl_required',
      label: 'Validation humaine requise (HITL)',
      type: 'toggle',
      description: 'Si activé, chaque action RPA nécessite une validation soignant avant exécution.',
    },
  ],
  'manager-agent': [
    {
      key: 'enabled',
      label: 'Activé',
      type: 'toggle',
    },
    {
      key: 'mode',
      label: 'Mode de génération',
      type: 'select',
      description: 'propose → stories dans inbox/ (validation requise). autonomous → stories dans in-progress/.',
      options: [
        { value: 'propose', label: 'propose — validation humaine requise' },
        { value: 'autonomous', label: 'autonomous — injection directe' },
      ],
    },
  ],
  'action-engine': [
    {
      key: 'enabled',
      label: 'Activé',
      type: 'toggle',
    },
  ],
  'dod-agent': [
    {
      key: 'enabled',
      label: 'Activé',
      type: 'toggle',
    },
  ],
};

// ── Champs de formulaire Bootstrap ───────────────────────────────────────────

function ToggleField({
  field,
  value,
  onChange,
}: {
  field: FieldDef;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="mb-3">
      <div className="form-check form-switch">
        <input
          className="form-check-input"
          type="checkbox"
          id={`field-${field.key}`}
          checked={!!value}
          onChange={e => onChange(e.target.checked)}
        />
        <label className="form-check-label fw-semibold small" htmlFor={`field-${field.key}`}>
          {field.label}
        </label>
      </div>
      {field.description && (
        <div className="form-text ms-4">{field.description}</div>
      )}
    </div>
  );
}

function NumberField({
  field,
  value,
  onChange,
}: {
  field: FieldDef;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="mb-3">
      <label htmlFor={`field-${field.key}`} className="form-label small fw-semibold">
        {field.label}
      </label>
      <div className="d-flex align-items-center gap-3">
        <input
          id={`field-${field.key}`}
          type="range"
          className="form-range flex-grow-1"
          min={field.min ?? 0}
          max={field.max ?? 100}
          step={field.step ?? 1}
          value={value}
          onChange={e => onChange(Number(e.target.value))}
        />
        <span
          className="badge bg-primary text-white"
          style={{ minWidth: '3.5rem', textAlign: 'center' }}
        >
          {value}
        </span>
      </div>
      {field.description && (
        <div className="form-text">{field.description}</div>
      )}
    </div>
  );
}

function SelectField({
  field,
  value,
  onChange,
}: {
  field: FieldDef;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="mb-3">
      <label htmlFor={`field-${field.key}`} className="form-label small fw-semibold">
        {field.label}
      </label>
      <select
        id={`field-${field.key}`}
        className="form-select form-select-sm"
        value={value}
        onChange={e => onChange(e.target.value)}
      >
        {(field.options ?? []).map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {field.description && (
        <div className="form-text">{field.description}</div>
      )}
    </div>
  );
}

// ── Fallback JSON brut ────────────────────────────────────────────────────────

function JsonFallbackForm({
  agentId,
  config,
  onSaved,
}: {
  agentId: string;
  config: AgentConfig;
  onSaved: (c: AgentConfig) => void;
}) {
  const [draft, setDraft] = useState(JSON.stringify(config, null, 2));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft(JSON.stringify(config, null, 2));
  }, [config]);

  async function handleSave() {
    setError(null);
    setSaving(true);
    try {
      const parsed: AgentConfig = JSON.parse(draft);
      const result = await agentsAdminService.patchConfig(agentId, parsed);
      onSaved(result.config);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'JSON invalide ou erreur serveur');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {saved && <div className="alert alert-success py-2 small">Configuration sauvegardée.</div>}
      {error && <div className="alert alert-danger py-2 small">{error}</div>}
      <p className="text-muted small mb-2">
        Configuration JSON. <strong>PHI interdit.</strong>
      </p>
      <textarea
        className="form-control font-monospace small"
        rows={8}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        spellCheck={false}
      />
      <div className="mt-2 d-flex justify-content-end">
        <button
          className="btn btn-primary btn-sm"
          onClick={handleSave}
          disabled={saving || draft === JSON.stringify(config, null, 2)}
        >
          {saving ? <><span className="spinner-border spinner-border-sm me-1" />Sauvegarde…</> : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}

// ── Formulaire principal ──────────────────────────────────────────────────────

export function AgentConfigForm({ agentId }: Props) {
  const [config, setConfig] = useState<AgentConfig>({});
  const [draft, setDraft] = useState<AgentConfig>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const schema = SCHEMAS[agentId];

  useEffect(() => {
    agentsAdminService.getConfig(agentId)
      .then(r => {
        setConfig(r.config);
        setDraft(r.config);
      })
      .catch(e => setError(e.message ?? 'Erreur chargement config'))
      .finally(() => setLoading(false));
  }, [agentId]);

  if (loading) return <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>;

  // Pas de schéma connu → fallback JSON
  if (!schema) {
    return (
      <JsonFallbackForm
        agentId={agentId}
        config={config}
        onSaved={(c) => setConfig(c)}
      />
    );
  }

  function updateField(key: string, value: unknown) {
    setDraft(prev => ({ ...prev, [key]: value }));
  }

  const isDirty = JSON.stringify(draft) !== JSON.stringify(config);

  async function handleSave() {
    setError(null);
    setSaving(true);
    try {
      const result = await agentsAdminService.patchConfig(agentId, draft);
      setConfig(result.config);
      setDraft(result.config);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    setDraft(config);
  }

  return (
    <div>
      {saved && <div className="alert alert-success py-2 small mb-3">Configuration sauvegardée.</div>}
      {error && <div className="alert alert-danger py-2 small mb-3">{error}</div>}

      {schema.map(field => {
        const val = draft[field.key];
        if (field.type === 'toggle') {
          return (
            <ToggleField
              key={field.key}
              field={field}
              value={typeof val === 'boolean' ? val : false}
              onChange={v => updateField(field.key, v)}
            />
          );
        }
        if (field.type === 'number') {
          return (
            <NumberField
              key={field.key}
              field={field}
              value={typeof val === 'number' ? val : (field.min ?? 0)}
              onChange={v => updateField(field.key, v)}
            />
          );
        }
        if (field.type === 'select') {
          return (
            <SelectField
              key={field.key}
              field={field}
              value={typeof val === 'string' ? val : (field.options?.[0]?.value ?? '')}
              onChange={v => updateField(field.key, v)}
            />
          );
        }
        return null;
      })}

      {isDirty && (
        <div className="d-flex gap-2 justify-content-end mt-3 pt-3 border-top">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={handleReset}
            disabled={saving}
          >
            Annuler
          </button>
          <button
            className="btn btn-sm btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <><span className="spinner-border spinner-border-sm me-1" />Sauvegarde…</> : 'Enregistrer'}
          </button>
        </div>
      )}
    </div>
  );
}
